import React, { useCallback, useEffect, useState } from "react";
import { AiOutlineDelete } from "react-icons/ai";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  addBanner,
  deleteBanner,
  editBanner,
  getAllBanner,
  getSingleBanner,
} from "../../redux/services/bannerSlice";
import { LuPencilLine } from "react-icons/lu";
import BannerForm from "./bannerForm";
import { useFormik } from "formik";
import * as Yup from "yup";
import { fileUpload } from "../../redux/services/fileUploadSlice";
import ConfirmationPage from "../../common/ConfirmationPage";
import Table from "../../common/Table";
import { Link } from "react-router-dom";

const Banner = () => {
  const dispatch = useDispatch();
  const getBannerData = useSelector((state) => state?.banner?.bannerData);
  const getSingleBannerData = useSelector(
    (state) => state?.banner?.singleBannerData
  );
  const [modelOpen, setModelOpen] = useState(false);
  const [editId, setEditId] = useState();
  const [itemToDelete, setItemToDelete] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);

  const getAllBannerApi = useCallback(async () => {
    try {
      await dispatch(getAllBanner());
    } catch (error) {}
  }, [dispatch]);

  useEffect(() => {
    getAllBannerApi();
  }, [getAllBannerApi]);

  const initialValues = {
    image: undefined,
    type: "home",
  };
  const validationSchema = Yup.object({
    image: Yup.mixed().required("Banner is required"),
  });
  const onSubmit = async (values) => {
    setLoading(true);
    const payload = { ...values };
    if (editId) {
      payload.bannerId = editId;
    }
    try {
      let response;
      if (editId) {
        response = await dispatch(editBanner(payload));
      } else {
        response = await dispatch(addBanner(payload));
      }
      if (response?.payload?.status === 200) {
        toast.success(response?.payload?.message, {
          autoClose: 2000,
          pauseOnHover: false,
        });
        setLoading(false);
        setModelOpen(false);
        resetForm();
        setEditId();
        setPreview();
        getAllBannerApi();
      } else {
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };
  const handleFileUpload = async (file) => {
    const formData = new FormData();
    formData.append("image", file);
    try {
      const response = await dispatch(fileUpload(formData));
      if (response) {
        setFieldValue("image", response?.payload);
      }
    } catch (error) {
      alert("Error uploading file. Please try again.");
    }
  };
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const imageURL = URL.createObjectURL(file);
      setPreview(imageURL);
      setFieldValue("image", imageURL);
      handleFileUpload(file);
    }
  };
  const deleteBannerData = async () => {
    try {
      const response = await dispatch(deleteBanner(itemToDelete));
      toast.success(response?.payload?.message, {
        autoClose: 2000,
        pauseOnHover: false,
      });
      setConfirm(false);
      getAllBannerApi();
    } catch (error) {}
  };
  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    setValues,
    setFieldValue,
    resetForm,
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
  });

  const getSingleBannerApi = useCallback(async () => {
    try {
      await dispatch(getSingleBanner(editId));
    } catch (error) {}
  }, [dispatch, editId]);

  useEffect(() => {
    if (editId) {
      getSingleBannerApi();
    }
  }, [editId, getSingleBannerApi]);

  useEffect(() => {
    if (getSingleBannerData) {
      setValues({
        image: getSingleBannerData?.image,
        type: getSingleBannerData?.type,
        link: getSingleBannerData?.link,
      });
    }
    setPreview(getSingleBannerData?.image);
  }, [getSingleBannerData, setValues]);
  const columns = [
    {
      header: "S/N",
      key: "index",
      render: (_, __, index) => index + 1,
    },
    {
      header: "Image",
      key: "image",
      render: (value) =>
        value ? (
          <div className="flex justify-center">
            <img src={value} alt="Banner" className="h-20 w-28" /> 
          </div>
        ) : (
          "—"
        ),
    },
    {
      header: "Link",
      key: "link",
      render: (value) =>
        value ? (
          <Link
            to={value}
            target="_blank"
            rel="noopener noreferrer"
            className={`p-4 border-btext-center ${
              value && "text-primary underline"
            }`}
          >
            {value}
          </Link>
        ) : (
          "—"
        ),
    },
    { header: "Type", key: "type" },
  ];

  const actions = [
    {
      icon: <LuPencilLine className="w-5 h-5" />,
      onClick: (row) => {
        setModelOpen(true);
        setEditId(row._id);
      },
    },
    {
      icon: <AiOutlineDelete className="w-6 h-6" />,
      onClick: (row) => {
        setItemToDelete(row._id);
        setConfirm(true);
      },
    },
  ];
  return (
    <div>
      <Table
        title="Banner"
        buttonTitle="Add New Banner"
        onAddClick={() => setModelOpen(true)}
        columns={columns}
        data={getBannerData?.banner_data}
        actions={actions}
      />
      {confirm && (
        <ConfirmationPage
          topicName="Banner"
          confirm={confirm}
          onDelete={deleteBannerData}
          onCancel={() => setConfirm(false)}
        />
      )}
      {modelOpen && (
        <BannerForm
          modelOpen={modelOpen}
          setModelOpen={setModelOpen}
          onClose={() => {
            setModelOpen(false);
            resetForm();
            setPreview();
            setEditId();
          }}
          values={values}
          handleChange={handleChange}
          editId={editId}
          handleBlur={handleBlur}
          errors={errors}
          touched={touched}
          handleSubmit={handleSubmit}
          loading={loading}
          handleImageChange={handleImageChange}
          preview={preview}
        />
      )}
    </div>
  );
};

export default Banner;
